"use client";

import { memo, type ReactNode } from "react";

interface FacepileSkeletonProps {
	count?: number;
	size?: "sm" | "md";
}

const sizeClasses = {
	sm: "h-5 w-5", // 20px - matches facepile size="sm"
	md: "h-8 w-8", // 32px - matches facepile size="md"
};

/**
 * Skeleton loader for facepile that exactly matches FacepileCore dimensions
 */
export const FacepileSkeleton = memo(function FacepileSkeleton({
	count = 3,
	size = "sm",
}: FacepileSkeletonProps) {
	return (
		<div className="inline-flex items-center leading-none">
			{Array.from({ length: count }).map((_, i) => (
				<div
					key={i}
					className="relative inline-flex leading-none"
					style={{ marginLeft: i === 0 ? 0 : "-0.5rem", zIndex: i + 1 }}
				>
					<div
						className={`${sizeClasses[size]} animate-pulse rounded-full border border-background bg-primary/10`}
					/>
				</div>
			))}
		</div>
	);
});

interface FacepileContainerProps {
	hasData: boolean;
	children: ReactNode;
}

/**
 * Container for facepile area
 * Only renders children when hasData is true
 * Does NOT show skeleton to avoid CLS from height mismatch (20px skeleton vs 17.5px text row)
 */
export const FacepileContainer = memo(function FacepileContainer({
	hasData,
	children,
}: FacepileContainerProps) {
	if (!hasData) {
		return null;
	}
	return <>{children}</>;
});
